import { ModalsProvider } from "@mantine/modals";
import { AppThemeProvider } from "./shared/context/AppTheme";
import { Notifications } from "@mantine/notifications";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { buildAbilityFromLegacy, type User } from "@yalana/contracts";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "./features/auth";
import { InstallPromptBanner } from "./features/pwa";
import i18n, { i18nReady } from "./i18n";
import { initTelemetry } from "./shared/infrastructure/telemetry";
import { routeTree } from "./routeTree.gen";
import { queryClient } from "./shared/infrastructure/query-client";
import { registerReloadOnPreloadError } from "./shared/utils/reload-on-preload-error";

// Mantine CSS — per-component imports instead of the global styles.css bundle.
// Only import CSS for components actually used in this app. Saves ~15-20 kB gzip
// vs the all-in-one styles.css (244 kB) which includes 50+ unused components.
import "@mantine/core/styles/default-css-variables.css";
import "@mantine/core/styles/baseline.css";
import "@mantine/core/styles/global.css";
// Layout
import "@mantine/core/styles/AppShell.css";
import "@mantine/core/styles/Center.css";
import "@mantine/core/styles/Flex.css";
import "@mantine/core/styles/Grid.css";
import "@mantine/core/styles/Group.css";
import "@mantine/core/styles/SimpleGrid.css";
import "@mantine/core/styles/Stack.css";
// Typography
import "@mantine/core/styles/Typography.css";
import "@mantine/core/styles/Text.css";
import "@mantine/core/styles/Title.css";
import "@mantine/core/styles/Anchor.css";
// Base primitives (required by composite components)
// Per Mantine docs: https://mantine.dev/styles/css-files-list/
import "@mantine/core/styles/Input.css";
import "@mantine/core/styles/InlineInput.css";
import "@mantine/core/styles/CloseButton.css";
import "@mantine/core/styles/Combobox.css";
import "@mantine/core/styles/FloatingIndicator.css";
import "@mantine/core/styles/ModalBase.css";
import "@mantine/core/styles/Overlay.css";
import "@mantine/core/styles/ScrollArea.css";
import "@mantine/core/styles/UnstyledButton.css";
import "@mantine/core/styles/VisuallyHidden.css";
// Form components
// Note: TextInput, Textarea, Select, NativeSelect, FileInput rely on Input.css +
// Combobox.css (already imported above) — no separate files.
// MultiSelect additionally needs Pill/PillsInput for selected item badges.
import "@mantine/core/styles/Checkbox.css";
import "@mantine/core/styles/PasswordInput.css";
import "@mantine/core/styles/Pill.css";
import "@mantine/core/styles/PillsInput.css";
import "@mantine/core/styles/Switch.css";
// Buttons & actions
import "@mantine/core/styles/ActionIcon.css";
import "@mantine/core/styles/Button.css";
// Feedback
import "@mantine/core/styles/Alert.css";
import "@mantine/core/styles/Badge.css";
import "@mantine/core/styles/Loader.css";
import "@mantine/core/styles/Notification.css";
import "@mantine/core/styles/Progress.css";
import "@mantine/core/styles/Skeleton.css";
// Overlays
import "@mantine/core/styles/Menu.css";
import "@mantine/core/styles/Modal.css";
import "@mantine/core/styles/Popover.css";
import "@mantine/core/styles/Tooltip.css";
// Surfaces
import "@mantine/core/styles/Card.css";
import "@mantine/core/styles/Divider.css";
import "@mantine/core/styles/Paper.css";
// Navigation & data
import "@mantine/core/styles/NavLink.css";
import "@mantine/core/styles/Pagination.css";
import "@mantine/core/styles/Table.css";
import "@mantine/core/styles/Tabs.css";
import "@mantine/core/styles/Timeline.css";
// Overlays (additional)
import "@mantine/core/styles/Burger.css";
import "@mantine/core/styles/Drawer.css";
import "@mantine/core/styles/Indicator.css";
// Misc
import "@mantine/core/styles/Accordion.css";
import "@mantine/core/styles/Code.css";
import "@mantine/core/styles/Container.css";
import "@mantine/core/styles/Image.css";
import "@mantine/core/styles/List.css";
import "@mantine/core/styles/NumberInput.css";
import "@mantine/core/styles/SegmentedControl.css";
import "@mantine/core/styles/ThemeIcon.css";
import "@mantine/notifications/styles.css";
// Inter — primary body typeface (brand guide). Self-hosted via @fontsource; the theme
// declares "Inter, sans-serif" but the font must actually be loaded or it falls back to
// system sans-serif. Weights: Light 300, Regular 400, Medium 500, Bold 700.
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/700.css";
import "./styles/global.css";

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: {
      user: null,
      ability: buildAbilityFromLegacy([], false),
      loading: true,
      authorizedTenants: [],
      signInWithPassword: async () => ({}) as User,
      signInWithOtp: async () => {},
      signUpWithMagicLink: async () => {},
      signOut: async () => {},
    },
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface RouterContext {
    queryClient: typeof queryClient;
    auth: {
      user: User | null;
      loading: boolean;
    };
  }
}

function syncDocumentMetadata(): void {
  document.documentElement.lang = i18n.language;
  document.title = i18n.t("app.title");
}

function registerServiceWorker(): void {
  if ("serviceWorker" in navigator && import.meta.env.PROD) {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // SW registration failure is non-blocking
    });
  }
}

async function bootstrap(): Promise<void> {
  registerServiceWorker();
  registerReloadOnPreloadError(window.location);
  initTelemetry();
  const uiVersion = import.meta.env.VITE_APP_VERSION?.substring(0, 7) || "dev";
  console.log(`%cYalana UI: ${uiVersion}`, "color: #868e96; font-size: 11px");

  await i18nReady;
  syncDocumentMetadata();
  i18n.on("languageChanged", syncDocumentMetadata);

  const hot = (import.meta as { hot?: { dispose: (cb: () => void) => void } })
    .hot;
  if (hot) {
    hot.dispose(() => {
      i18n.off("languageChanged", syncDocumentMetadata);
    });
  }

  const el = document.querySelector<HTMLDivElement>("#app");
  if (!el) throw new Error("#app not found");
  createRoot(el).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppThemeProvider>
          <Notifications position="top-right" />
          <InstallPromptBanner />
          <ModalsProvider>
            <AuthProvider router={router}>
              <RouterProvider router={router} />
            </AuthProvider>
          </ModalsProvider>
        </AppThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
